/**
 * The shell: a 64px top bar and a left icon rail, which is the shape Courtyard
 * uses. The rail collapses to a horizontal scroller under `lg` rather than
 * hiding behind a hamburger — on a shop, the categories *are* the navigation.
 */
import { useState, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { Avatar } from './Avatar'
import { DevLogin, devLoginAvailable } from './DevLogin'
import { HeaderTicker } from './JustPulled'
import { ContractPill, TokenBanner } from './TokenBanner'
import { useAuth } from '../hooks/useAuth'
import { useMarket } from '../hooks/queries'
import { cents, dateOnly, shortAddress } from '../lib/format'
import { SOCIALS, TELEGRAM_HANDLE, X_HANDLE } from '../lib/links'

interface NavItem {
  to: string
  label: string
  icon: ReactNode
  end?: boolean
  /** Marks a destination that exists but is not built yet, e.g. "Soon". */
  badge?: string
}

/* Inline single-path icons: no icon dependency, no flash of missing glyphs. */
const icon = (d: string) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="size-5">
    <path d={d} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/* Both marks are filled glyphs rather than stroked outlines, so neither can go
   through `icon()` above. Same reasoning otherwise: inline, no dependency. */
function XIcon({ className = 'size-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

function TelegramIcon({ className = 'size-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M21.94 4.3 18.9 19.2c-.23 1.02-.84 1.27-1.7.79l-4.7-3.46-2.27 2.18c-.25.25-.46.46-.94.46l.34-4.78 8.7-7.86c.38-.34-.08-.53-.59-.19L6.0 13.11l-4.63-1.45c-1.01-.31-1.03-1 .21-1.49l18.1-6.98c.84-.31 1.57.19 1.26 1.11Z" />
    </svg>
  )
}

/**
 * An icon link in the top bar. The dim-until-hover treatment is shared rather
 * than repeated per network, so adding the next one is a line, not a copy.
 */
function HeaderSocial({
  href,
  label,
  handle,
  children,
}: {
  href: string
  label: string
  handle: string
  children: ReactNode
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={label}
      title={handle}
      className="grid size-8 place-items-center transition-colors"
      style={{ color: 'var(--color-faint)' }}
      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-text)')}
      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-faint)')}
    >
      {children}
    </a>
  )
}

const PRIMARY: NavItem[] = [
  { to: '/', label: 'Home', end: true, icon: icon('M3 10.5 12 3l9 7.5M5 9.5V21h14V9.5') },
  { to: '/packs', label: 'Packs', icon: icon('M3 7.5 12 3l9 4.5v9L12 21l-9-4.5v-9ZM3 7.5 12 12m0 0 9-4.5M12 12v9') },
  { to: '/market', label: 'Tokens', icon: icon('M12 3 5 7v10l7 4 7-4V7l-7-4Zm0 5.5L9 10.5v3L12 15.5l3-2v-3L12 8.5Z') },
]

const SECONDARY: NavItem[] = [
  { to: '/me', label: 'Dashboard', icon: icon('M4 13h6V4H4zM14 20h6v-9h-6zM4 20h6v-4H4zM14 8h6V4h-6z') },
  { to: '/vault', label: 'Vault', icon: icon('M4 5h16v14H4zM9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0M12 9V7m0 10v-2') },
  { to: '/token', label: '$ELYXR', icon: icon('M12 2 4 7v10l8 5 8-5V7l-8-5Zm0 6.5L8.5 10.5v3L12 15.5l3.5-2v-3L12 8.5Z') },
  {
    to: '/studio',
    label: 'Studio',
    badge: 'Soon',
    icon: icon('M4 20h16M6 16l6-11 6 11M9.5 12h5'),
  },
  { to: '/referrals', label: 'Refer', icon: icon('M16 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM8 21v-2a4 4 0 0 1 4-4h8M6 8v6M3 11h6') },
  { to: '/wallet', label: 'Wallet', icon: icon('M3 7h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Zm0 0V6a2 2 0 0 1 2-2h11M16 13h2') },
  { to: '/leaderboard', label: 'Leaderboard', icon: icon('M7 20V10m5 10V4m5 16v-6') },
  { to: '/fairness', label: 'Fairness', icon: icon('M12 3v18M5 8h14M7 8l-3 6h6l-3-6Zm10 0-3 6h6l-3-6Z') },
]

export function Layout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col">
      {/* Above the sticky header, not inside it. Rail and MobileNav are both
          positioned off a hardcoded 101px header height, so a bar added within
          it would have to move three numbers in lockstep; out here it simply
          scrolls away and the header sticks to the top as before. */}
      <TokenBanner />
      <TopBar />
      {/* Outside the flex row below: as a sibling of <main> this would be a
          flex item, and a flex item's automatic minimum size is its content —
          so the scroller would push the page wide instead of scrolling. */}
      <MobileNav />
      <div className="flex w-full flex-1 gap-0">
        <Rail />
        <main className="min-w-0 flex-1 px-4 pb-20 lg:px-6">{children}</main>
      </div>
      <Footer />
    </div>
  )
}

function TopBar() {
  const { user, isConnected, needsSignature, isSigningIn, connectAndSignIn, signOut, openAccount, error } =
    useAuth()

  return (
    /* Fully opaque. The ambient backdrop is saturated enough that even 15%
       transparency drags neon through the wordmark and the balance, and a blur
       cannot rescue contrast it has already lost. */
    <header className="sticky top-0 z-40 border-b" style={{ background: 'var(--color-bg)' }}>
      <div className="flex h-16 w-full items-center gap-4 px-4 lg:px-6">
        <NavLink to="/" className="flex items-center gap-2.5" aria-label="Elyxr home">
          <Logo />
          <span className="text-[1.0625rem] font-bold tracking-tight">Elyxr</span>
        </NavLink>

        <ContractPill />

        <div className="flex shrink-0 items-center">
          <HeaderSocial href={SOCIALS.x} label={`Elyxr on X (${X_HANDLE})`} handle={X_HANDLE}>
            <XIcon />
          </HeaderSocial>
          <HeaderSocial
            href={SOCIALS.telegram}
            label={`Elyxr on Telegram (${TELEGRAM_HANDLE})`}
            handle={TELEGRAM_HANDLE}
          >
            <TelegramIcon />
          </HeaderSocial>
        </div>

        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          {user && <BalancePill balanceCents={user.balanceCents} />}

          {user ? (
            <div className="flex items-center gap-2">
              {/* The account button is the avatar plus the handle, so the
                  identity in the header is the same one the ticker shows. */}
              <NavLink to="/me" className="btn btn-ghost btn-sm gap-2">
                <Avatar
                  identity={{
                    username: user.username,
                    handle: user.username ?? shortAddress(user.address),
                    avatarKind: user.avatarKind,
                    avatarRef: user.avatarRef,
                    seed: user.avatarSeed,
                  }}
                  size={20}
                />
                <span className="hidden sm:inline">
                  {user.username ?? shortAddress(user.address)}
                </span>
              </NavLink>
              <button className="btn btn-quiet btn-sm hidden sm:inline-flex" onClick={openAccount}>
                Wallet
              </button>
              <button className="btn btn-quiet btn-sm" onClick={() => void signOut()}>
                Sign out
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {devLoginAvailable && <DevLogin />}
              <button
                className="btn btn-primary btn-sm"
                onClick={() => void connectAndSignIn()}
                disabled={isSigningIn}
              >
                {isSigningIn
                  ? 'Check your wallet…'
                  : needsSignature
                    ? 'Sign in'
                    : isConnected
                      ? 'Sign in'
                      : 'Connect wallet'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Fixed height regardless of loading state — Rail and MobileNav are
          both positioned assuming a constant total header height, and a
          strip that only appears once the feed loads would leave a gap
          under them for one paint. */}
      <div className="flex h-9 items-center border-t px-4 lg:px-6">
        <HeaderTicker />
      </div>

      {error && (
        <div
          className="border-t px-4 py-2 text-center text-xs lg:px-6"
          style={{ background: 'var(--color-surface-2)', color: 'var(--color-down)' }}
          role="alert"
        >
          {error}
        </div>
      )}
    </header>
  )
}

/**
 * Balance, and the one control that changes it.
 *
 * The only rounded thing on the site, and deliberately so: everything else is
 * square by choice (see the radius tokens), which is exactly what makes a pill
 * read as a control rather than as another panel. `rounded-full` is a Tailwind
 * literal and is not driven by those zeroed tokens, so it survives the theme.
 *
 * The plus is a real link rather than a decoration on the balance — topping up
 * is the action people come to the header for, and making them find it in a nav
 * list is the kind of friction that only ever costs deposits.
 */
function BalancePill({ balanceCents }: { balanceCents: number }) {
  return (
    <div
      className="hidden items-center gap-1 rounded-full py-1 pr-1 pl-3 sm:flex"
      style={{ background: 'var(--color-brand)', color: '#0a1a05' }}
    >
      <span className="tnum text-sm font-bold whitespace-nowrap">
        {cents(balanceCents, { compact: true })}
      </span>
      <NavLink
        to="/wallet"
        aria-label="Add funds"
        title="Add funds"
        className="balance-add grid size-6 shrink-0 place-items-center rounded-full transition-colors"
        style={{ background: 'rgb(0 0 0 / 0.22)', color: 'inherit' }}
      >
        <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
          <path
            d="M12 5v14M5 12h14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
          />
        </svg>
      </NavLink>
    </div>
  )
}

function Logo({ size = 32 }: { size?: number }) {
  return (
    <img
      src="/brand/exlyr-mark-256.png"
      alt=""
      aria-hidden
      width={size}
      height={size}
      style={{ width: size, height: size }}
      className="shrink-0 object-contain"
    />
  )
}


/** Shared by the desktop rail and the mobile scroller. */
function useNavLink() {
  const location = useLocation()
  // NavLink's own active check ignores the query string, so activeness is
  // computed here — a nav entry that carries one must not match without it.
  const current = `${location.pathname}${location.search}`

  const link = (item: NavItem) => {
    const active = item.to.includes('?')
      ? current === item.to
      : location.pathname === item.to ||
        (!item.end && location.pathname.startsWith(item.to) && !location.search)

    return (
      <NavLink
        key={item.to}
        to={item.to}
        className="flex shrink-0 items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
        style={{
          background: active ? 'var(--color-surface-2)' : undefined,
          color: active ? 'var(--color-text)' : 'var(--color-muted)',
        }}
      >
        <span style={{ color: active ? 'var(--color-brand)' : 'inherit' }}>{item.icon}</span>
        <span className="whitespace-nowrap">{item.label}</span>
        {item.badge && (
          <span
            className="ml-auto shrink-0 px-1.5 py-0.5 text-[0.5625rem] font-bold tracking-wider uppercase"
            style={{ background: 'var(--color-brand-wash)', color: 'var(--color-brand)' }}
          >
            {item.badge}
          </span>
        )}
      </NavLink>
    )
  }

  return link
}

function Rail() {
  const link = useNavLink()

  return (
    <nav
      // 101px: the header's 64px row plus its 36px ticker strip plus the
      // header's own 1px bottom border. Both this offset and the height
      // below have to move together with the header's actual height, or one
      // opens a gap under the header and the other overflows past the
      // viewport.
      className="sticky top-[101px] hidden h-[calc(100dvh-101px)] w-60 shrink-0 flex-col gap-1 overflow-y-auto border-r px-3 py-5 lg:flex"
      style={{ background: 'var(--color-bg)' }}
    >
      {PRIMARY.map(link)}
      <div className="my-3 border-t" />
      {SECONDARY.map(link)}
      <div className="mt-auto pt-4">
        <MarketFooterNote />
      </div>
    </nav>
  )
}

function MobileNav() {
  const link = useNavLink()

  return (
    <nav
      // See the matching comment on `Rail` — 101px is the header's real
      // total height (main row + ticker strip + border), not just the row.
      className="no-scrollbar sticky top-[101px] z-30 flex w-full gap-1 overflow-x-auto border-b px-4 py-2 lg:hidden"
      style={{ background: 'var(--color-bg)' }}
    >
      {[...PRIMARY, ...SECONDARY].map(link)}
    </nav>
  )
}

function MarketFooterNote() {
  const { data } = useMarket()
  if (!data) return null
  return (
    <div className="text-[0.6875rem] leading-relaxed" style={{ color: 'var(--color-faint)' }}>
      <div className="tnum">{data.count} drawable tokens</div>
      <div className="mt-0.5">Priced {dateOnly(data.refreshedAt)}</div>
    </div>
  )
}

function Footer() {
  const { data } = useMarket()

  return (
    <footer className="mt-auto border-t" style={{ background: 'var(--color-bg)' }}>
      <div
        className="grid w-full gap-8 px-4 py-10 text-xs lg:grid-cols-3 lg:px-6"
        style={{ color: 'var(--color-muted)' }}
      >
        <div>
          <div className="flex items-center gap-2">
            <Logo />
            <span className="text-sm font-bold" style={{ color: 'var(--color-text)' }}>
              Elyxr
            </span>
          </div>
          <p className="mt-3 max-w-sm leading-relaxed">
            Packs of real equities tokens. Every pack publishes its return-to-player, its
            full odds table, and a seed commitment you can check after the fact.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href={SOCIALS.x}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-2 border px-3 py-1.5 font-medium transition-colors"
              style={{ borderColor: 'var(--color-line)', color: 'var(--color-text)' }}
            >
              <XIcon className="size-3.5" />
              {X_HANDLE}
            </a>
            <a
              href={SOCIALS.telegram}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-2 border px-3 py-1.5 font-medium transition-colors"
              style={{ borderColor: 'var(--color-line)', color: 'var(--color-text)' }}
            >
              <TelegramIcon className="size-3.5" />
              {TELEGRAM_HANDLE}
            </a>
          </div>

          {data && (
            <p className="mt-4 leading-relaxed">
              {data.count} tokens priced {dateOnly(data.refreshedAt)}
              {data.source ? ` · ${data.source}` : ''}
            </p>
          )}
        </div>

        <div>
          <div className="eyebrow">Before you open anything</div>
          <p className="mt-3 leading-relaxed">
            Packs are a negative-expectation product by design. Published RTP runs 54–63%, so the
            expected result of opening repeatedly is a loss, and the median pack returns
            considerably less than the average one. Decide a budget first.
          </p>
        </div>

        <div>
          <div className="eyebrow">This build</div>
          <p className="mt-3 leading-relaxed">
            Balances and positions are simulated. Cards are priced against real market snapshots but
            settle to a play-money ledger. Nothing here buys, sells, or custodies a real token, and
            nothing is an offer or a recommendation to trade one.
          </p>
        </div>
      </div>
    </footer>
  )
}

/** Full-bleed section wrapper used by the home page. */
export function Section({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return <section className={`py-8 ${className}`}>{children}</section>
}

export function useHeaderAuth() {
  const [open, setOpen] = useState(false)
  return { open, setOpen }
}
